import ActionTypeEnum from "../../enums/ActionTypeEnum";
import CartClient from "../../clients/cart/CartClient";
import Cart from "../../interfaces/cart/Cart";
import CartPreferences from "../../interfaces/cart/CartPreferences";
import Action from "../../interfaces/Action";
import GetCartConfig from "../../interfaces/cart/GetCartConfig";
import UpdateCartCustomerResponse from "../../interfaces/cart/UpdateCartCustomerResponse";
import ThunkAction from "../../interfaces/ThunkAction";
import CartBrowserStorageService from "../../services/CartBrowserStorageService";
import eventEmitter from "../../events/eventEmitter";
import { ApplePayCustomerConfig } from "../../interfaces/apple-pay";
import UserSessionUpdatedEvent from "../../events/events/UserSessionUpdatedEvent";

// ------------------------- [ Action Creators ] -------------------------

export const setCart = (cart: Cart | null): Action => {
    // Keep browser storage in sync with the cart in state.
    try {
        if (cart) {
            CartBrowserStorageService.setCartUuid(cart.cart_uuid);
            CartBrowserStorageService.setQuantitySum(cart.quantity_sum);
        } else {
            CartBrowserStorageService.removeCartUuid();
            CartBrowserStorageService.removeQuantitySum();
        }
    } catch (error) {
        console.error("Storage: Failed to modify cart UUID and/or quantity sum.", error);
    }

    return {
        type: ActionTypeEnum.CART_SET,
        cart,
    };
};

const setEmail = (email: string): Action => ({
    type: ActionTypeEnum.CART_CUSTOMER_EMAIL_SET,
    email,
});

export const setPreferences = (preferences: CartPreferences): Action => ({
    type: ActionTypeEnum.CART_PREFERENCES_SET,
    preferences,
});

export const setPreferencesPartially = (preferences: CartPreferences): Action => ({
    type: ActionTypeEnum.CART_PREFERENCES_SET_PARTIALLY,
    preferences,
});

// ------------------------- [ Thunks ] -------------------------

const cartClient = new CartClient();

export const loadCart = (cartUuid: string, config: GetCartConfig = {
    do_validate: true,
    do_remove_erroneous_products: true,
}): ThunkAction<Promise<Cart>> => (
    async (dispatch) => {
        const response = await cartClient.getCart(cartUuid, config);
        dispatch(setCart(response.data));
        console.debug("Successfully loaded cart.", cartUuid, config);

        return response.data;
    }
);

export const updateEmail = (cart: Cart, email: string): ThunkAction<Promise<UpdateCartCustomerResponse>> => (
    async (dispatch) => {
        const { data } = await cartClient.updateCustomerEmail(cart.cart_uuid, email);
        dispatch(setEmail(email));

        // For performance, this endpoint doesn't return a full cart object, so push this cart object in memory containing the new email values.
        eventEmitter.emit(new UserSessionUpdatedEvent({
            ...cart,
            customer_email: data.customer_email,
            customer_email_hash: data.customer_email_hash,
        }));

        return data;
    }
);

export const setApplePayCustomer = (cart: Cart, payload: ApplePayCustomerConfig): ThunkAction<Promise<Cart>> => (
    async (dispatch) => {
        const response = await cartClient.setApplePayCustomer(cart.cart_uuid, payload);
        dispatch(setCart(response.data));

        eventEmitter.emit(new UserSessionUpdatedEvent(response.data));

        return response.data;
    }
);
